import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "@/components/ui/sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { ArrowRight, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const ASSET_TYPES = [
    "Fixed Platform",
    "Semi-Submersible Rig",
    "Drillship",
    "FPSO / Vessel",
    "Jack-up Rig",
    "Wind / Offshore Substation",
    "Other",
];

const inputCls =
    "bg-abyss border-border rounded-none h-12 text-foreground placeholder:text-steel/60 focus-visible:ring-safety focus-visible:ring-1 focus-visible:ring-offset-0 focus-visible:border-safety";

export default function InquiryForm() {
    const [form, setForm] = useState({
        name: "",
        company: "",
        email: "",
        phone: "",
        asset_type: "",
        message: "",
    });
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error("Please fill in name, email and message.");
            return;
        }
        setLoading(true);
        try {
            await api.post("/inquiries", form);
            setDone(true);
            toast.success("Assessment request received. Our offshore desk will respond within 24h.");
            setForm({ name: "", company: "", email: "", phone: "", asset_type: "", message: "" });
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section id="contact" data-testid="contact-section" className="bg-surface py-24 lg:py-32 border-t border-border">
            <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
                <div className="lg:col-span-5">
                    <span className="font-mono-tech text-xs uppercase tracking-[0.3em] text-safety">
                        / Request Assessment
                    </span>
                    <h2 className="mt-4 font-heading font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.9]">
                        Get an offshore HVAC assessment.
                    </h2>
                    <p className="mt-6 text-steel leading-relaxed max-w-md">
                        Tell us about your asset and the systems you need serviced. Our
                        offshore engineering desk responds to every request within 24 hours.
                    </p>
                    <div className="mt-10 space-y-4 font-mono-tech text-xs uppercase tracking-[0.2em] text-steel">
                        <p>ops@kevaservice.com</p>
                        <p>+44 (0) 20 8000 0000</p>
                        <p className="text-safety">24 / 7 EMERGENCY DISPATCH</p>
                    </div>
                </div>

                <div className="lg:col-span-7">
                    <motion.form
                        data-testid="inquiry-form"
                        onSubmit={submit}
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                        className="border border-border bg-abyss p-8 lg:p-10 clip-plate"
                    >
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <Label className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-steel">
                                    Full name *
                                </Label>
                                <Input data-testid="input-name" className={inputCls} value={form.name} onChange={set("name")} placeholder="Jane Doe" />
                            </div>
                            <div className="space-y-2">
                                <Label className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-steel">
                                    Company
                                </Label>
                                <Input data-testid="input-company" className={inputCls} value={form.company} onChange={set("company")} placeholder="Operator / EPC" />
                            </div>
                            <div className="space-y-2">
                                <Label className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-steel">
                                    Email *
                                </Label>
                                <Input data-testid="input-email" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@company.com" />
                            </div>
                            <div className="space-y-2">
                                <Label className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-steel">
                                    Phone
                                </Label>
                                <Input data-testid="input-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+44 ..." />
                            </div>
                            <div className="space-y-2 sm:col-span-2">
                                <Label className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-steel">
                                    Asset type
                                </Label>
                                <Select value={form.asset_type} onValueChange={(v) => setForm({ ...form, asset_type: v })}>
                                    <SelectTrigger data-testid="select-asset" className={inputCls}>
                                        <SelectValue placeholder="Select platform / vessel type" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-surface border-border text-foreground">
                                        {ASSET_TYPES.map((t) => (
                                            <SelectItem key={t} value={t} data-testid={`asset-opt-${t}`}>
                                                {t}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2 sm:col-span-2">
                                <Label className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-steel">
                                    Scope of work *
                                </Label>
                                <Textarea
                                    data-testid="input-message"
                                    className={`${inputCls} h-32 py-3`}
                                    value={form.message}
                                    onChange={set("message")}
                                    placeholder="Describe the systems, location and timeline..."
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            data-testid="submit-inquiry"
                            disabled={loading}
                            className="mt-8 group inline-flex items-center gap-2 bg-safety hover:bg-safety-hover disabled:opacity-60 text-white font-mono-tech text-xs uppercase tracking-[0.15em] px-8 py-4 transition-colors"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" /> Transmitting
                                </>
                            ) : done ? (
                                "Send another request"
                            ) : (
                                <>
                                    Submit request
                                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                                </>
                            )}
                        </button>
                    </motion.form>
                </div>
            </div>
        </section>
    );
}
